/**
 * Conversao de WebVTT para a lista de frases que o modulo `transcript` consome.
 *
 * Story-2499: a transcricao de aula de video vem do vdplayer ja em JSON
 * (`{ frases: [{ begin, end, text }] }`). Aula de audio nao tem transmissao no WCS,
 * entao a transcricao dela chega como WebVTT cru do EAD e precisa ser convertida
 * para o mesmo formato — assim a aba de transcricao, a busca e o clique para dar
 * seek funcionam identicos nas duas origens, sem ramificacao na UI.
 */

/**
 * Ex.: "00:01:02.500 --> 00:01:05.000" (WebVTT usa ponto; SRT usa virgula).
 *
 * A hora e OPCIONAL e aceita mais de dois digitos: a especificacao do WebVTT permite
 * "02.340 --> 07.120" na forma MM:SS, e midias com mais de 99 horas existiriam com tres.
 * O formatador do nosso servico sempre escreve HH:MM:SS, mas recusar as outras formas
 * faria o parser devolver lista vazia — indistinguivel de "aula sem transcricao".
 */
const LINHA_DE_TEMPO = /^(?:(\d{2,}):)?(\d{2}):(\d{2})[.,](\d{3})\s*-->\s*(?:(\d{2,}):)?(\d{2}):(\d{2})[.,](\d{3})/;

/**
 * Converte um WebVTT em frases com tempos em segundos.
 *
 * @param {string} vtt conteudo do arquivo
 * @returns {Array<{begin: number, end: number, text: string}>} vazio se nao houver nada legivel
 *
 * @example
 * parseWebVtt('WEBVTT\n\n1\n00:00:02.340 --> 00:00:07.120\nola mundo\n')
 * // [{ begin: 2.34, end: 7.12, text: 'ola mundo' }]
 */
export function parseWebVtt (vtt) {
  if (!vtt || typeof vtt !== 'string') return [];

  const frases = [];
  // \r\n e \n no mesmo split: o arquivo e gerado no Windows, mas pode trafegar normalizado.
  const linhas = vtt.split(/\r?\n/);

  let atual = null;

  for (const linha of linhas) {
    const tempos = LINHA_DE_TEMPO.exec(linha);

    if (tempos) {
      if (atual) frases.push(atual);
      atual = {
        begin: paraSegundos(tempos[1], tempos[2], tempos[3], tempos[4]),
        end: paraSegundos(tempos[5], tempos[6], tempos[7], tempos[8]),
        text: ''
      };

      continue;
    }

    if (!atual) continue;

    // Linha em branco fecha a legenda. O numero sequencial que antecede a proxima cai
    // fora porque nesse ponto `atual` ja e null. Num VTT malformado, sem a linha em
    // branco entre cues, o numero acaba anexado ao texto da legenda anterior — aceito:
    // preferimos texto com um digito a mais do que descartar a transcricao inteira.
    if (linha.trim() === '') {
      frases.push(atual);
      atual = null;
      continue;
    }

    atual.text = atual.text ? `${atual.text} ${linha.trim()}` : linha.trim();
  }

  if (atual) frases.push(atual);

  return frases.filter(f => f.text.length > 0);
}

/** `horas` vem undefined na forma curta MM:SS, que a especificacao permite. */
function paraSegundos (horas, minutos, segundos, milissegundos) {
  return (Number(horas || 0) * 3600) + (Number(minutos) * 60) + Number(segundos) + (Number(milissegundos) / 1000);
}
